import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Divider,
  Chip,
  Avatar,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Business, Add as AddIcon, ShoppingCart } from "@mui/icons-material";
import PhotoUpload from "../common/PhotoUpload";
import { supabaseApi } from "../../services/supabaseApi";
import { Item, Category, Location, Merchant, ItemStatus } from "@catalog/types";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface ItemFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any, photos?: File[]) => void;
  initialData?: Item;
  mode?: "create" | "edit";
}

const ItemForm: React.FC<ItemFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  mode = "create",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    purchase_price: 0,
    purchase_date: "",
    category_id: "",
    merchant_id: "",
    notes: "",
    status: "in_storage" as ItemStatus,
    location_id: "",
  });

  const [photos, setPhotos] = useState<File[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [photosToDelete, setPhotosToDelete] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);

  // Add validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>("");

  // Add loading states for dropdowns
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownErrors, setDropdownErrors] = useState<Record<string, string>>(
    {}
  );

  // Update form data and photos when initialData changes or form opens
  useEffect(() => {
    if (open) {
      if (initialData && mode === "edit") {
        setFormData({
          name: initialData.name || "",
          description: initialData.description || "",
          purchase_price: initialData.purchase_price || 0,
          purchase_date: initialData.purchase_date || "",
          category_id: initialData.category_id || "",
          merchant_id: initialData.merchant_id || "",
          notes: initialData.notes || "",
          status: initialData.status || ("in_storage" as ItemStatus),
          location_id: initialData.location_id || "",
        });
        setExistingPhotos(
          initialData && Array.isArray(initialData.photo_url)
            ? initialData.photo_url
            : []
        );
      } else {
        // Reset form for new item
        setFormData({
          name: "",
          description: "",
          purchase_price: 0,
          purchase_date: "",
          category_id: "",
          merchant_id: "",
          notes: "",
          status: "in_storage" as ItemStatus,
          location_id: "",
        });
        setExistingPhotos([]);
      }

      // Clear errors when form opens
      setErrors({});
      setSubmitError("");
    }
  }, [initialData, mode, open]);

  useEffect(() => {
    const loadData = async () => {
      setLoadingDropdowns(true);
      setDropdownErrors({});

      try {
        const results = await Promise.allSettled([
          supabaseApi.categories.getAll(),
          supabaseApi.locations.getAll(),
          supabaseApi.merchants.getAll(),
        ]);

        // Handle categories
        if (results[0].status === "fulfilled") {
          setCategories(results[0].value);
        } else {
          console.error("Failed to load categories:", results[0].reason);
          setDropdownErrors((prev) => ({
            ...prev,
            categories: "Failed to load categories",
          }));
        }

        // Handle locations
        if (results[1].status === "fulfilled") {
          setLocations(results[1].value);
        } else {
          console.error("Failed to load locations:", results[1].reason);
          setDropdownErrors((prev) => ({
            ...prev,
            locations: "Failed to load locations",
          }));
        }

        // Handle merchants
        if (results[2].status === "fulfilled") {
          setMerchants(results[2].value);
        } else {
          console.error("Failed to load merchants:", results[2].reason);
          setDropdownErrors((prev) => ({
            ...prev,
            merchants: "Failed to load merchants",
          }));
        }
      } catch (error) {
        console.error("Error loading dropdown data:", error);
        setDropdownErrors({
          categories: "Failed to load categories",
          locations: "Failed to load locations",
          merchants: "Failed to load merchants",
        });
      } finally {
        setLoadingDropdowns(false);
      }
    };

    if (open) {
      loadData();
      // Reset photo deletion tracking when form opens
      setPhotosToDelete([]);
      setPhotos([]);
    }
  }, [open]);

  // Validation functions
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "name":
        if (!value || value.trim().length === 0) {
          return "Item name is required";
        }
        if (value.trim().length > 255) {
          return "Item name must be less than 255 characters";
        }
        break;
      case "category_id":
        if (!value) {
          return "Category is required";
        }
        break;
      case "location_id":
        if (!value) {
          return "Location is required";
        }
        break;
      case "purchase_date":
        if (!value) {
          return "Purchase date is required";
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          return "Please enter a valid date";
        }
        if (date > new Date()) {
          return "Purchase date cannot be in the future";
        }
        break;
      case "purchase_price":
        if (value === "" || value === null || value === undefined) {
          return "Purchase price is required";
        }
        const price = Number(value);
        if (isNaN(price)) {
          return "Purchase price must be a valid number";
        }
        if (price < 0) {
          return "Purchase price cannot be negative";
        }
        if (price > 1000000) {
          return "Purchase price seems too high (max: $1,000,000)";
        }
        break;
      case "description":
        if (value && value.length > 1000) {
          return "Description must be less than 1000 characters";
        }
        break;
      case "notes":
        if (value && value.length > 1000) {
          return "Notes must be less than 1000 characters";
        }
        break;
    }
    return "";
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate all required fields
    Object.keys(formData).forEach((field) => {
      const error = validateField(
        field,
        formData[field as keyof typeof formData]
      );
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setSubmitError("");

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Prevent multiple submissions
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      // Prepare submit data with proper type conversion
      const submitData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        notes: formData.notes?.trim() || null,
        purchase_price: Number(formData.purchase_price),
        merchant_id: formData.merchant_id || null,
        // Ensure proper date format (YYYY-MM-DD)
        purchase_date: formData.purchase_date,
      };

      // Additional validation for parsed data
      if (isNaN(submitData.purchase_price)) {
        setSubmitError("Invalid purchase price");
        return;
      }

      let result;
      if (mode === "create") {
        result = await supabaseApi.items.create(submitData, photos);
        if (!result) {
          throw new Error("Failed to create item - no data returned");
        }
      } else {
        if (!initialData?.id) {
          throw new Error("Cannot update item - no item ID provided");
        }
        result = await supabaseApi.items.update(
          initialData.id,
          submitData,
          photos,
          photosToDelete
        );
        if (!result) {
          throw new Error("Failed to update item - no data returned");
        }
      }

      // Success - call parent callback and close form
      onSubmit(result, photos);
      onClose();

      // Reset form state for next use
      setErrors({});
      setSubmitError("");
    } catch (error) {
      console.error("Error saving item:", error);

      // Provide user-friendly error messages
      let errorMessage = "Failed to save item. Please try again.";

      if (error instanceof Error) {
        if (error.message.includes("User not authenticated")) {
          errorMessage = "Please log in to add items to inventory.";
        } else if (error.message.includes("Network")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else if (
          error.message.includes("Failed to create") ||
          error.message.includes("Failed to update")
        ) {
          errorMessage = error.message;
        } else if (error.message.includes("PGRST")) {
          errorMessage = "Database error. Please try again or contact support.";
        }
      }

      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedMerchant = merchants.find((m) => m.id === formData.merchant_id);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={isMobile ? "sm" : "lg"}
        fullWidth
        fullScreen={isMobile} // Full screen on mobile for better space usage
      >
        <DialogTitle
          sx={{
            pb: { xs: 1, md: 2 }, // Reduced padding on mobile
            fontSize: { xs: "1.125rem", md: "1.25rem" }, // Smaller title on mobile
          }}
        >
          {mode === "create" ? "Add New Item" : "Edit Item"}
        </DialogTitle>
        <DialogContent
          sx={{
            px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
            pt: { xs: 1, md: 2 }, // Reduced top padding on mobile
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ pt: { xs: 1, md: 2 } }}
          >
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {" "}
              {/* Reduced spacing on mobile */}
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    mb: { xs: 1, md: 2 }, // Reduced margin on mobile
                    fontSize: { xs: "1rem", md: "1.25rem" }, // Smaller text on mobile
                    fontWeight: 600,
                  }}
                >
                  Basic Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Item Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  required
                  placeholder="Enter item name"
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  required
                  error={!!errors.category_id || !!dropdownErrors.categories}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category_id}
                    label="Category"
                    onChange={(e) =>
                      handleInputChange("category_id", e.target.value)
                    }
                    disabled={loading || loadingDropdowns}
                  >
                    {loadingDropdowns ? (
                      <MenuItem disabled>Loading categories...</MenuItem>
                    ) : dropdownErrors.categories ? (
                      <MenuItem disabled>Error loading categories</MenuItem>
                    ) : categories.length === 0 ? (
                      <MenuItem disabled>No categories available</MenuItem>
                    ) : (
                      categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {(errors.category_id || dropdownErrors.categories) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, mx: 1.75 }}
                    >
                      {errors.category_id || dropdownErrors.categories}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  multiline
                  rows={3}
                  placeholder="Describe the item..."
                  error={!!errors.description}
                  helperText={errors.description}
                  disabled={loading}
                />
              </Grid>
              {/* Financial Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: { xs: 1, md: 2 } }} />{" "}
                {/* Reduced margin on mobile */}
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    mb: { xs: 1, md: 2 }, // Reduced margin on mobile
                    fontSize: { xs: "1rem", md: "1.25rem" }, // Smaller text on mobile
                    fontWeight: 600,
                  }}
                >
                  Financial Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Purchase Date"
                  value={
                    formData.purchase_date
                      ? new Date(formData.purchase_date)
                      : null
                  }
                  onChange={(date) =>
                    handleInputChange(
                      "purchase_date",
                      date ? date.toISOString().split("T")[0] : ""
                    )
                  }
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.purchase_date,
                      helperText: errors.purchase_date,
                      disabled: loading,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Purchase Price"
                  type="number"
                  value={formData.purchase_price}
                  onChange={(e) =>
                    handleInputChange("purchase_price", e.target.value)
                  }
                  required
                  error={!!errors.purchase_price}
                  helperText={errors.purchase_price}
                  disabled={loading}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Grid>
              {/* Organization & Merchant */}
              <Grid item xs={12}>
                <Divider sx={{ my: { xs: 1, md: 2 } }} />{" "}
                {/* Reduced margin on mobile */}
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    mb: { xs: 1, md: 2 }, // Reduced margin on mobile
                    fontSize: { xs: "1rem", md: "1.25rem" }, // Smaller text on mobile
                    fontWeight: 600,
                  }}
                >
                  Organization & Merchant
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  required
                  error={!!errors.location_id || !!dropdownErrors.locations}
                >
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={formData.location_id}
                    label="Location"
                    onChange={(e) =>
                      handleInputChange("location_id", e.target.value)
                    }
                    disabled={loading || loadingDropdowns}
                  >
                    {loadingDropdowns ? (
                      <MenuItem disabled>Loading locations...</MenuItem>
                    ) : dropdownErrors.locations ? (
                      <MenuItem disabled>Error loading locations</MenuItem>
                    ) : locations.length === 0 ? (
                      <MenuItem disabled>No locations available</MenuItem>
                    ) : (
                      locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {(errors.location_id || dropdownErrors.locations) && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, mx: 1.75 }}
                    >
                      {errors.location_id || dropdownErrors.locations}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) =>
                      handleInputChange("status", e.target.value as ItemStatus)
                    }
                    disabled={loading}
                  >
                    <MenuItem value="staged">Staged</MenuItem>
                    <MenuItem value="in_storage">In Storage</MenuItem>
                    <MenuItem value="unavailable">Unavailable</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Merchant */}
              <Grid item xs={12}>
                <FormControl fullWidth error={!!dropdownErrors.merchants}>
                  <InputLabel>Merchant/Store</InputLabel>
                  <Select
                    value={formData.merchant_id}
                    label="Merchant/Store"
                    onChange={(e) =>
                      handleInputChange("merchant_id", e.target.value)
                    }
                    disabled={loading || loadingDropdowns}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <em>No Merchant</em>;
                      }
                      const merchant = merchants.find((m) => m.id === selected);
                      return merchant ? merchant.name : selected;
                    }}
                  >
                    <MenuItem value="">
                      <em>No Merchant</em>
                    </MenuItem>
                    {loadingDropdowns ? (
                      <MenuItem disabled>Loading merchants...</MenuItem>
                    ) : dropdownErrors.merchants ? (
                      <MenuItem disabled>Error loading merchants</MenuItem>
                    ) : (
                      merchants.map((merchant) => (
                        <MenuItem key={merchant.id} value={merchant.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            <Avatar
                              src={merchant.image_url}
                              sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: merchant.color || "#1976d2",
                              }}
                            >
                              {merchant.name[0]}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {merchant.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {merchant.type || "Store"}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {dropdownErrors.merchants && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, mx: 1.75 }}
                    >
                      {dropdownErrors.merchants}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {/* Selected Merchant Display */}
              {selectedMerchant && (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: { xs: 1.5, md: 2 }, // Reduced padding on mobile
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }} // Smaller text on mobile
                    >
                      Selected Merchant
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1.5, md: 2 },
                      }}
                    >
                      <Avatar
                        src={selectedMerchant.image_url}
                        sx={{
                          width: { xs: 32, md: 40 }, // Smaller avatar on mobile
                          height: { xs: 32, md: 40 },
                          backgroundColor: selectedMerchant.color || "#1976d2",
                        }}
                      >
                        {selectedMerchant.name[0]}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ fontSize: { xs: "0.875rem", md: "0.875rem" } }}
                        >
                          {selectedMerchant.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }} // Smaller caption on mobile
                        >
                          {selectedMerchant.type || "Store"} •{" "}
                          {selectedMerchant.website || "No website"}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: "auto" }}>
                        <Chip
                          size="small"
                          label={
                            selectedMerchant.is_active ? "Active" : "Inactive"
                          }
                          sx={{
                            backgroundColor:
                              (selectedMerchant.color || "#1976d2") + "20",
                            color: selectedMerchant.color || "#1976d2",
                            fontSize: { xs: "0.65rem", md: "0.75rem" }, // Smaller chip text on mobile
                            height: { xs: 20, md: 24 }, // Smaller chip on mobile
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              )}
              {/* Photos */}
              <Grid item xs={12}>
                <Divider sx={{ my: { xs: 1, md: 2 } }} />{" "}
                {/* Reduced margin on mobile */}
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    mb: { xs: 1, md: 2 }, // Reduced margin on mobile
                    fontSize: { xs: "1rem", md: "1.25rem" }, // Smaller text on mobile
                    fontWeight: 600,
                  }}
                >
                  Photos
                </Typography>
                <PhotoUpload
                  photos={existingPhotos}
                  onPhotosChange={(photos, files, deletedPhotos) => {
                    if (files) setPhotos(files);
                    if (deletedPhotos) setPhotosToDelete(deletedPhotos);
                  }}
                  maxPhotos={5}
                  disabled={loading}
                />
              </Grid>
              {/* Notes */}
              <Grid item xs={12}>
                <Divider sx={{ my: { xs: 1, md: 2 } }} />{" "}
                {/* Reduced margin on mobile */}
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  multiline
                  rows={isMobile ? 2 : 3} // Fewer rows on mobile
                  placeholder="Any additional notes about this item..."
                  error={!!errors.notes}
                  helperText={errors.notes}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        {/* Submit Error Display */}
        {submitError && (
          <Box sx={{ px: { xs: 2, md: 3 }, pb: 1 }}>
            <Typography
              variant="body2"
              color="error"
              sx={{
                p: 1.5,
                bgcolor: "error.light",
                color: "error.contrastText",
                borderRadius: 1,
                fontSize: "0.875rem",
              }}
            >
              {submitError}
            </Typography>
          </Box>
        )}

        <DialogActions
          sx={{
            p: { xs: 2, md: 3 }, // Reduced padding on mobile
            pt: { xs: 1, md: 1 },
            gap: { xs: 1, md: 0 }, // Add gap on mobile
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
              px: { xs: 2, md: 3 }, // Reduced padding on mobile
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              minWidth: { xs: 100, md: 120 }, // Smaller min width on mobile
              fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
              px: { xs: 2, md: 3 }, // Reduced padding on mobile
            }}
            onClick={handleSubmit}
          >
            {loading
              ? "Saving..."
              : mode === "create"
                ? "Create Item"
                : "Update Item"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ItemForm;
